/**
 * Grid de números de un talonario (presentacional + acciones opcionales).
 *
 * Replica el grid del SSR `talonario_orders` / `historical_talonario_orders`
 * (`progress/explore_talonarios_ssr.md`, §2.3/§2.6): cada número se pinta en verde
 * (orden real), amarillo (marcado manual) o gris (disponible), con su contenido
 * (orden+cliente+estado, o autor de la marca). En modo editable, un número
 * disponible ofrece "Marcar" y uno marcado "Desmarcar"; en modo `readOnly`
 * (histórico) no hay botones.
 *
 * Presentacional: recibe el grid y los callbacks de marcar/desmarcar (que la
 * pantalla cablea a las mutaciones). No hace `fetch`. Usa `components/ui`.
 */
import { Button } from "@/components/ui";

import { GRID_STATUS_LABEL } from "../constants";
import type { GridNumber, GridNumberStatus } from "../types";

interface TalonarioGridViewProps {
  numbers: GridNumber[];
  /** Modo solo lectura (histórico): sin botones de marcar/desmarcar. */
  readOnly?: boolean;
  /** Marca un número disponible (no en `readOnly`). */
  onMark?: (number: number) => void;
  /** Desmarca un número marcado manualmente (no en `readOnly`). */
  onUnmark?: (number: number) => void;
  /** Número con una acción en curso (deshabilita su botón). */
  pendingNumber?: number | null;
}

/** Estilos del recuadro de cada número, por estado. */
const CELL_STYLE: Record<GridNumberStatus, string> = {
  order: "border-emerald-300 bg-emerald-50",
  marked: "border-amber-300 bg-amber-50",
  available: "border-slate-200 bg-white",
};

export function TalonarioGridView({
  numbers,
  readOnly = false,
  onMark,
  onUnmark,
  pendingNumber = null,
}: TalonarioGridViewProps) {
  if (numbers.length === 0) {
    return (
      <p className="px-1 py-8 text-center text-sm text-slate-500">
        No hay números en este rango.
      </p>
    );
  }

  return (
    <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      {numbers.map((item) => (
        <li
          key={item.number}
          className={`flex flex-col gap-1 rounded-md border p-3 text-sm ${CELL_STYLE[item.status]}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-base font-semibold text-slate-900">
              {item.number}
            </span>
            <span className="text-xs text-slate-500">
              {GRID_STATUS_LABEL[item.status]}
            </span>
          </div>

          {item.status === "order" && item.order && (
            <div className="text-xs text-slate-600">
              <p className="font-medium text-slate-800">#{item.order.order_number}</p>
              <p className="truncate">{item.order.customer_name}</p>
              <p className="capitalize">{item.order.status}</p>
            </div>
          )}

          {item.status === "marked" && item.marked && (
            <p className="text-xs text-slate-600">
              Marcado por {item.marked.marked_by}
            </p>
          )}

          {!readOnly && item.status === "available" && onMark && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onMark(item.number)}
              disabled={pendingNumber === item.number}
            >
              Marcar
            </Button>
          )}

          {!readOnly && item.status === "marked" && onUnmark && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onUnmark(item.number)}
              disabled={pendingNumber === item.number}
            >
              Desmarcar
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}
